/**
 * Auth Store
 * Enterprise-grade authentication state management
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { authService } from '@/services/auth';
import { meService } from '@/services/me';
import type {
  AuthSlice,
  User,
  Role,
  Permission,
} from '@/types';

// Server is authoritative for role + permissions (delivered via /me).
// The client no longer maintains a duplicate permission map — doing so
// invited drift and hardcoded defaults. The only client-side fallback is
// a safe baseline used when /me fails or omits the fields entirely.
const SAFE_FALLBACK_ROLE: Role = 'viewer';
const SAFE_FALLBACK_PERMISSIONS: Permission[] = [];

// ============================================
// Dev Mode - Use dev-login endpoint
// ============================================
const isLocalhost = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname.startsWith('192.168.')
);

export const DEV_MODE_ENABLED = isLocalhost && import.meta.env.DEV;

// Dev admin email from env (should match backend ADMIN_EMAIL)
const DEV_ADMIN_EMAIL = import.meta.env.VITE_DEV_ADMIN_EMAIL;

// Session configuration
const SESSION_CONFIG = {
  idleTimeoutMs: 30 * 60 * 1000,      // 30 minutes
  absoluteTimeoutMs: 12 * 60 * 60 * 1000, // 12 hours
  refreshThreshold: 0.2,
  warningBeforeMs: 5 * 60 * 1000,
};

// Initial state
const initialState = {
  user: null,
  organization: null,
  role: SAFE_FALLBACK_ROLE,
  permissions: SAFE_FALLBACK_PERMISSIONS,
  isAdmin: false,
  adminRole: null as string | null,
  isAuthenticated: false,
  isLoading: true,
  sessionChecked: false,
  sessionExpiresAt: null,
  lastActivity: Date.now(),
  error: null,
};

/**
 * Auth store with enterprise features
 */
export const useAuthStore = create<AuthSlice>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      ...initialState,

      /**
       * Check if user has a valid session
       * Called on app initialization
       */
      checkSession: async () => {
        set({ isLoading: true, error: null });

        // DEV MODE: Create dev session via dev-login endpoint
        if (DEV_MODE_ENABLED && DEV_ADMIN_EMAIL) {
          console.log('🔓 Dev mode: Creating dev session');
          console.log(`📧 Using admin email: ${DEV_ADMIN_EMAIL}`);
          
          // Call dev-login endpoint to create a real session
          const devLoginSuccess = await authService.devLogin(DEV_ADMIN_EMAIL);
          
          if (!devLoginSuccess) {
            console.warn('⚠️ Dev login failed, falling back to normal auth flow');
            // Fall through to normal session check
          } else {
            console.log('✅ Dev session created, checking session...');
            // Continue to normal session check (will use the newly created session)
          }
        }

        try {
          // Check if session exists - this endpoint bypasses auth middleware
          // and returns user_id + email if authenticated
          const sessionResponse = await authService.checkSession();

          if (!sessionResponse.authenticated || !sessionResponse.user_id) {
            set({
              ...initialState,
              isLoading: false,
              sessionChecked: true,
            });
            return false;
          }

          // Build user from session; then hydrate profile + RBAC from GET /me.
          // Server is authoritative for role/permissions — no client-side
          // hardcoding or secondary /admin/me round-trip.
          const user: User = {
            user_id: sessionResponse.user_id,
            email: sessionResponse.email || '',
            first_name: null,
            last_name: null,
            email_verified: true,
            organization_id: null,
            profile_picture_url: null,
          };

          const profile = await meService.getMe();
          let role: Role = SAFE_FALLBACK_ROLE;
          let permissions: Permission[] = SAFE_FALLBACK_PERMISSIONS;
          let isAdmin = false;
          let adminRole: string | null = null;

          if (profile) {
            user.first_name = profile.first_name;
            user.last_name = profile.last_name;
            user.profile_picture_url = profile.profile_picture_url ?? null;

            // /me is the single source of truth for RBAC. Any missing/null
            // field falls back to the safe baseline — we never synthesize.
            if (profile.role) role = profile.role;
            if (Array.isArray(profile.permissions)) {
              permissions = profile.permissions as Permission[];
            }
            isAdmin = Boolean(profile.is_admin);
            adminRole = profile.admin_role ?? null;
          } else {
            console.warn(
              '[auth] /me returned null — user has session but no profile. ' +
              'Falling back to safe role/permissions (viewer / []).'
            );
          }

          set({
            user,
            role,
            permissions,
            isAdmin,
            adminRole,
            isAuthenticated: true,
            isLoading: false,
            sessionChecked: true,
            sessionExpiresAt: Date.now() + SESSION_CONFIG.absoluteTimeoutMs,
            lastActivity: Date.now(),
            error: null,
          });

          return true;
        } catch (error) {
          console.error('Session check error:', error);
          set({
            ...initialState,
            isLoading: false,
            sessionChecked: true,
            error: 'Failed to verify session',
          });
          return false;
        }
      },

      /**
       * Logout user
       * Clears session and redirects to login
       */
      logout: async () => {
        try {
          await authService.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Always clear local state
          set({
            ...initialState,
            isLoading: false,
            sessionChecked: true,
          });

          // Broadcast logout to other tabs
          broadcastAuthEvent('LOGOUT');
        }
      },

      /**
       * Refresh session
       * Called periodically to extend session
       */
      refreshSession: async () => {
        const { isAuthenticated } = get();
        if (!isAuthenticated) return;

        try {
          const sessionResponse = await authService.checkSession();

          if (!sessionResponse.authenticated) {
            // Session expired - logout
            get().logout();
            return;
          }

          // Update expiry time
          set({
            sessionExpiresAt: Date.now() + SESSION_CONFIG.absoluteTimeoutMs,
            lastActivity: Date.now(),
          });

          // Broadcast refresh to other tabs
          broadcastAuthEvent('SESSION_REFRESH');
        } catch (error) {
          console.error('Session refresh error:', error);
        }
      },

      /**
       * Update last activity timestamp
       * Called on user interaction
       */
      updateLastActivity: () => {
        set({ lastActivity: Date.now() });
      },

      /**
       * Check if user has a specific permission
       */
      hasPermission: (permission: Permission) => {
        const { permissions } = get();
        return permissions.includes(permission);
      },

      /**
       * Check if user has any of the specified permissions
       */
      hasAnyPermission: (perms: Permission[]) => {
        const { permissions } = get();
        return perms.some((p) => permissions.includes(p));
      },

      /**
       * Check if user has all specified permissions
       */
      hasAllPermissions: (perms: Permission[]) => {
        const { permissions } = get();
        return perms.every((p) => permissions.includes(p));
      },

      /**
       * Set user (used for updates)
       */
      setUser: (user: User | null) => {
        set({ user });
      },

      /**
       * Set loading state
       */
      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      /**
       * Set error state
       */
      setError: (error: string | null) => {
        set({ error });
      },

      /**
       * Clear all auth state
       */
      clearAuth: () => {
        set({
          ...initialState,
          isLoading: false,
          sessionChecked: true,
        });
      },
    })),
    { name: 'AuthStore' }
  )
);

// ============================================
// Multi-Tab Sync via BroadcastChannel
// ============================================

const AUTH_CHANNEL_NAME = 'semaphoric_auth';
let authChannel: BroadcastChannel | null = null;

/**
 * Initialize multi-tab sync
 */
export function initAuthChannel(): void {
  if (typeof BroadcastChannel === 'undefined') {
    console.warn('BroadcastChannel not supported - multi-tab sync disabled');
    return;
  }

  try {
    authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);

    authChannel.onmessage = (event) => {
      const { type } = event.data;

      switch (type) {
        case 'LOGOUT':
          // Another tab logged out - clear this tab's state
          useAuthStore.getState().clearAuth();
          break;

        case 'SESSION_REFRESH':
          // Another tab refreshed - update our timestamp
          useAuthStore.setState({
            sessionExpiresAt: Date.now() + SESSION_CONFIG.absoluteTimeoutMs,
          });
          break;

        case 'SESSION_EXPIRED':
          // Session expired in another tab
          useAuthStore.getState().clearAuth();
          break;

        case 'LOGIN':
          // New login in another tab - refresh our state
          useAuthStore.getState().checkSession();
          break;
      }
    };
  } catch (error) {
    console.error('Failed to initialize auth channel:', error);
  }
}

/**
 * Broadcast auth event to other tabs
 */
function broadcastAuthEvent(type: string, payload?: unknown): void {
  if (!authChannel) return;

  try {
    authChannel.postMessage({
      type,
      timestamp: Date.now(),
      payload,
    });
  } catch (error) {
    console.error('Failed to broadcast auth event:', error);
  }
}

/**
 * Cleanup auth channel
 */
export function closeAuthChannel(): void {
  if (authChannel) {
    authChannel.close();
    authChannel = null;
  }
}

// ============================================
// Selectors (optimized with useShallow)
// ============================================

/**
 * Select auth state for components
 */
export const useAuth = () =>
  useAuthStore(
    useShallow((state) => ({
      user: state.user,
      isAdmin: state.isAdmin,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      sessionChecked: state.sessionChecked,
      error: state.error,
      checkSession: state.checkSession,
      logout: state.logout,
    }))
  );

/**
 * Select user info only
 */
export const useUser = () =>
  useAuthStore(
    useShallow((state) => ({
      user: state.user,
      organization: state.organization,
      role: state.role,
    }))
  );

/**
 * Select permissions for access control
 */
export const usePermissions = () =>
  useAuthStore(
    useShallow((state) => ({
      permissions: state.permissions,
      hasPermission: state.hasPermission,
      hasAnyPermission: state.hasAnyPermission,
      hasAllPermissions: state.hasAllPermissions,
    }))
  );

/**
 * Select session info for timeout management
 */
export const useSession = () =>
  useAuthStore(
    useShallow((state) => ({
      sessionExpiresAt: state.sessionExpiresAt,
      lastActivity: state.lastActivity,
      updateLastActivity: state.updateLastActivity,
      refreshSession: state.refreshSession,
    }))
  );

